import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const TermsOfService = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-900">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-20">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>
      </div>

      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
        }}></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-4xl bg-slate-800/80 backdrop-blur-lg border-slate-700 shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent flex items-center justify-center gap-2">
              <FileText className="w-8 h-8 text-purple-400" />
              {t('termsOfService')}
            </CardTitle>
            <CardDescription className="text-slate-300">
              {t('lastUpdated')}: 2024年12月19日
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6 text-slate-300 max-h-96 overflow-y-auto">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. {t('serviceDescription')}</h2>
              <p className="text-sm leading-relaxed">
                {t('serviceDescriptionText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. {t('userAccounts')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('userAccountsText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('accountSecurity')}</li>
                <li>{t('accurateInfo')}</li>
                <li>{t('unauthorizedUse')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. {t('acceptableUse')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('acceptableUseText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('noIllegalActivity')}</li>
                <li>{t('noHarmfulContent')}</li>
                <li>{t('noSystemInterference')}</li>
                <li>{t('respectIntellectualProperty')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. {t('dataAndPrivacy')}</h2>
              <p className="text-sm leading-relaxed">
                {t('dataPrivacyText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. {t('paymentAndBilling')}</h2>
              <p className="text-sm leading-relaxed mb-2">
                {t('paymentBillingText')}
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t('monthlyBilling')}</li>
                <li>{t('autoRenewal')}</li>
                <li>{t('noRefunds')}</li>
                <li>{t('priceChanges')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. {t('intellectualProperty')}</h2>
              <p className="text-sm leading-relaxed">
                {t('intellectualPropertyText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. {t('serviceAvailability')}</h2>
              <p className="text-sm leading-relaxed">
                {t('serviceAvailabilityText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. {t('limitationOfLiability')}</h2>
              <p className="text-sm leading-relaxed">
                {t('limitationLiabilityText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. {t('termination')}</h2>
              <p className="text-sm leading-relaxed">
                {t('terminationText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. {t('governingLaw')}</h2>
              <p className="text-sm leading-relaxed">
                {t('governingLawText')}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">11. {t('contactInformation')}</h2>
              <p className="text-sm leading-relaxed">
                {t('contactInformationText')}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
